import React from 'react';
import { motion } from 'framer-motion';
import { BookMockup } from './BookMockup';
import { CheckCircle2, FileText, Gift, ChevronRight } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
export function Hero() {
  return <section className="relative pt-8 pb-20 lg:pt-10 lg:pb-28 overflow-hidden" data-id="element-228">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-sand/50 rounded-bl-[100px] -z-10" data-id="element-229" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-230">
        {/* Logo */}
        <div className="mb-10 -ml-4" data-id="element-231">
          <img src="https://cdn.magicpatterns.com/uploads/rgU7gUPBFU366a1fsk78Bg/Gemini_Generated_Image_voy4efvoy4efvoy4.png" alt="Epikka Store" className="h-72 object-contain mix-blend-multiply" data-id="element-232" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-233">
          {/* Left: Mockup */}
          <motion.div className="relative flex justify-center lg:justify-center items-center" initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-234">
            <div className="relative" data-id="element-235">
              <div className="absolute -inset-4 bg-sage/20 rounded-full blur-3xl -z-10" data-id="element-236" />
              <BookMockup title="Primeros Bocados" subtitle="Guía Integral para Nutrir el Cuerpo y el Vínculo de tu Bebé" size="lg" className="z-10" data-id="element-237" />

              {/* Bonus mockups around main book */}
              <motion.div className="absolute -left-20 top-8 z-0" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.4,
              duration: 0.6
            }} data-id="element-238">
                <BookMockup title="Plan Semanal" variant="bonus1" size="sm" data-id="element-239" />
              </motion.div>

              <motion.div className="absolute -right-16 top-16 z-0" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5,
              duration: 0.6
            }} data-id="element-240">
                <BookMockup title="La Despensa" variant="bonus2" size="sm" data-id="element-241" />
              </motion.div>

              <motion.div className="absolute -right-10 -bottom-16 z-0" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6,
              duration: 0.6
            }} data-id="element-242">
                <BookMockup title="30 Recetas" variant="bonus3" size="sm" data-id="element-243" />
              </motion.div>

              {/* Floating badge */}
              <motion.div className="absolute -bottom-10 -left-10 glass-panel p-4 rounded-xl flex items-center gap-3 z-20" initial={{
              y: 20,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.7,
              duration: 0.6
            }} data-id="element-244">
                <div className="bg-mauve/20 p-2 rounded-full" data-id="element-245">
                  <Gift className="w-5 h-5 text-mauve" data-id="element-246" />
                </div>
                <div data-id="element-247">
                  <p className="text-xs font-bold text-charcoal" data-id="element-248">
                    Incluye 3 bonos
                  </p>
                  <p className="text-[10px] text-warmGray" data-id="element-249">Valorados en $47</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <div className="flex flex-col items-start space-y-8" data-id="element-250">
            <motion.div className="flex items-center text-xs text-warmGray tracking-wider uppercase" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-251">
              <span data-id="element-252">Inicio</span>
              <ChevronRight className="w-3 h-3 mx-2" data-id="element-253" />
              <span data-id="element-254">Tienda</span>
              <ChevronRight className="w-3 h-3 mx-2" data-id="element-255" />
              <span className="text-charcoal font-medium" data-id="element-256">Ebook</span>
            </motion.div>

            <motion.div className="space-y-4" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-257">
              <h1 className="text-5xl lg:text-6xl font-serif text-charcoal leading-tight" data-id="element-258">
                Primeros Bocados
              </h1>
              <p className="text-xl text-warmGray font-serif italic" data-id="element-259">
                De la leche a la mesa: recetas y tranquilidad.
              </p>
              <p className="text-sm font-medium text-olive bg-olive/10 px-3 py-1 rounded-full inline-block" data-id="element-260">
                Guía práctica para empezar la alimentación complementaria con
                más calma, seguridad y confianza
              </p>
            </motion.div>

            <motion.div className="flex gap-3" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-261">
              <span className="flex items-center gap-1.5 text-xs font-medium bg-white border border-sand px-3 py-1.5 rounded-md shadow-sm" data-id="element-262">
                <FileText className="w-3.5 h-3.5 text-sage" data-id="element-263" />
                Formato digital PDF
              </span>
              <span className="flex items-center gap-1.5 text-xs font-medium bg-white border border-sand px-3 py-1.5 rounded-md shadow-sm" data-id="element-264">
                <Gift className="w-3.5 h-3.5 text-mauve" data-id="element-265" />
                Incluye 3 bonos
              </span>
            </motion.div>

            <motion.p className="text-base text-warmGray leading-relaxed" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-266">
              Una guía clara, cálida y basada en criterios actualizados para
              acompañarte en el inicio de la alimentación complementaria de tu
              bebé. Menos miedo, menos confusión y más confianza en cada bocado.
            </motion.p>

            {/* Purchase Box */}
            <motion.div className="w-full bg-white border border-sand rounded-2xl p-6 shadow-sm" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} data-id="element-267">
              <div className="flex justify-between items-center mb-6" data-id="element-268">
                <span className="text-sm font-medium text-olive flex items-center gap-2" data-id="element-269">
                  <span className="w-2 h-2 rounded-full bg-olive animate-pulse" data-id="element-270" />
                  Disponible ahora
                </span>
                <div className="flex items-center gap-3" data-id="element-271">
                  <span className="bg-terracotta/15 text-terracotta text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-272">
                    -66%
                  </span>
                  <span className="text-lg text-warmGray/60 line-through font-serif" data-id="element-273">
                    $29.00
                  </span>
                  <span className="text-3xl font-serif text-charcoal" data-id="element-274">
                    $9.99
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3" data-id="element-275">
                <a href="https://epikkastore.com/cart/45054208213015:1" target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} className="flex-1 bg-olive hover:bg-olive/90 text-white px-6 py-3.5 rounded-lg font-medium transition-colors shadow-sm flex justify-center items-center gap-2" data-id="element-276">
                  Comprar ahora
                </a>
                <button className="flex-1 bg-sand hover:bg-sand/80 text-charcoal px-6 py-3.5 rounded-lg font-medium transition-colors" onClick={() => document.getElementById('que-incluye')?.scrollIntoView({
                behavior: 'smooth'
              })} data-id="element-277">
                  Ver lo que incluye
                </button>
              </div>
            </motion.div>

            {/* Quick Benefits */}
            <motion.div className="grid grid-cols-2 gap-4 w-full pt-4" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} data-id="element-278">
              {['Señales de preparación', 'Seguridad y prevención', 'Nutrición con propósito', 'Recetas y recursos prácticos'].map((benefit, i) => <div key={i} className="flex items-center gap-2 text-sm text-warmGray" data-id="element-279">
                  <CheckCircle2 className="w-4 h-4 text-sage shrink-0" data-id="element-280" />
                  <span data-id="element-281">{benefit}</span>
                </div>)}
            </motion.div>

            {/* Refund guarantee */}
            <motion.div className="flex items-center gap-2 w-full pt-2" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.7
          }} data-id="element-282">
              <svg className="w-4 h-4 text-olive shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-283">
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" data-id="element-284" />
                <path d="m9 12 2 2 4-4" data-id="element-285" />
              </svg>
              <p className="text-xs text-warmGray" data-id="element-286">
                <span className="font-medium text-charcoal" data-id="element-287">
                  Garantía de reembolso de 7 días
                </span>{' '}
                — Si no estás satisfecho/a, te devolvemos tu dinero.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}